<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import CartProductNotification from '~/components/cart/CartProductNotification.vue'
import CartQuantityCounter from '~/components/cart/CartQuantityCounter.vue'
import BasicButton from '~/components/form-elements/BasicButton.vue'
import ConfirmDeletion from '~/components/modal-windows/ConfirmDeletion.vue'
import DiscountLabel from '~/components/products/DiscountLabel.vue'
import Multiplicity from '~/components/products/Multiplicity.vue'
import ProductImage from '~/components/products/ProductImage.vue'
import ProductImageDecorator from '~/components/products/ProductImageDecorator.vue'
import { useProduct } from '~/composables/product'
import constants from '~/constants'
import ImageType from '~/enums/image-type'
import helpers from '~/helpers'
import formatter from '~/helpers/formatter'
import type { CartProductData } from '~/types/cart'

const props = defineProps<{
  cartProductData: CartProductData
}>()

const emit = defineEmits<{
  (e: 'deleteProductFromCart', id: string): void
  (e: 'setProductQuantity', id: string, quantity: number): void
  (e: 'toggleProductIsSelected', id: string, isSelected: boolean): void
}>()

const imageTypes = [ImageType.Thumbnail240p, ImageType.Thumbnail360p, ImageType.Thumbnail480p, ImageType.Original]

const { toggleIsInWishList } = useProduct()

const availableProductsCount = computed(() => props.cartProductData.meta.availableProductsCount)
const discountedPrice = computed(() => helpers.getProductDiscountedPrice(props.cartProductData))
const discountInKopecks = computed(() => props.cartProductData.meta.discountInKopecks)
const isSelected = computed({
  get () {
    return props.cartProductData.meta.isSelected
  },
  set (isSelected) {
    emit('toggleProductIsSelected', props.cartProductData.id, isSelected!)
  }
})
const multiplicity = computed(() => props.cartProductData.attributes.multiplicity ?? 1)
const quantity = computed({
  get () {
    return props.cartProductData.meta.quantity
  },
  set (x) {
    const { cartProductData } = props

    if (x !== cartProductData.meta.quantity) {
      emit('setProductQuantity', cartProductData.id, x)
    }

    return x
  }
})

function deleteProductFromCart (id: string): void {
  emit('deleteProductFromCart', id)
}

async function openConfirmDeletionModalWindow (): Promise<void> {
  await useModal({
    component: ConfirmDeletion,
    attrs: {
      title: constants.confirmDeletionTexts.cartProduct,
      onConfirmDeletion: (): void => deleteProductFromCart(props.cartProductData.id)
    }
  }).open()
}
</script>

<template>
  <div class="flex w-full select-none flex-col">
    <div class="flex w-full flex-wrap items-stretch justify-between py-3 lg:flex-nowrap">
      <div class="order-1 flex w-[calc(100%-28px)] items-start pr-3 lg:w-full">
        <ProductImageDecorator class="mr-3">
          <ProductImage :image-types="imageTypes" :product-data="cartProductData" />
        </ProductImageDecorator>

        <div class="md:max-w-2/3 max-w-full lg:min-w-[196px] xl:min-w-[338px] 2xl:min-w-[360px]">
          <NuxtLink
            class="
              line-clamp-3 block text-xs font-medium leading-[140%] text-[#434242]
              sm:text-sm md:text-base
            "
            :title="cartProductData.attributes.name"
            :to="helpers.getProductPath(cartProductData)"
          >
            {{ cartProductData.attributes.name }}
          </NuxtLink>

          <Multiplicity class="mt-2" :multiplicity="multiplicity" />
        </div>
      </div>

      <div class="order-3 flex justify-center lg:order-2 lg:w-full">
        <div class="flex flex-col sm:flex-row lg:flex-col xl:flex-row">
          <div class="my-4 flex items-center sm:my-0 lg:items-start">
            <BasicButton
              :aria-label="'Удалить из\xA0корзины'"
              class="mr-2 md:mr-3"
              color="light-gray"
              icon="outlined/close"
              is-icon-only
              is-red-on-hover
              is-small
              @click="openConfirmDeletionModalWindow"
            />

            <BasicButton
              :aria-label="cartProductData.meta.isInWishList ? 'Удалить из\xA0«Избранного»' : 'Добавить в\xA0«Избранное»'"
              :color="cartProductData.meta.isInWishList ? 'red' : 'light-gray'"
              :icon="cartProductData.meta.isInWishList ? 'outlined/favorite' : 'outlined/love'"
              is-icon-only
              is-red-on-hover
              is-small
              @click="toggleIsInWishList(cartProductData)"
            />
          </div>

          <div class="mx-0 flex items-center whitespace-nowrap sm:mx-4 lg:mx-0 lg:items-start xl:mx-6">
            <CartQuantityCounter
              v-model="quantity"
              is-small
              :max="
                (
                  availableProductsCount === undefined
                  || availableProductsCount === null
                    ? 1000
                    : availableProductsCount
                )
              "
              :multiplicity="multiplicity"
            />
          </div>
        </div>
      </div>

      <div
        class="
          order-4 mr-8 mt-[70px] flex flex-col items-end justify-end
          sm:mt-3.5 sm:min-w-[115px] lg:order-3 lg:-mr-8 lg:-mt-0.5 xl:mr-0 xl:justify-start
        "
      >
        <PriceWithPromoCode
          class="whitespace-nowrap [&_*]:!text-base"
          :discount-in-kopecks="discountInKopecks"
          mode="cart-product"
          :need-to-round-price-with-promo-code="false"
          :product-data="cartProductData"
          :quantity="quantity"
          :with-discounts="false"
        />

        <div class="mt-1 flex items-center">
          <DiscountLabel :product-data="cartProductData" />

          <div
            v-if="quantity > 1"
            class="ml-2 mt-0.5 whitespace-nowrap text-xs font-medium text-[#a0a29f] md:font-normal"
          >
            {{ formatter.formatPrice(discountedPrice) }}&nbsp;₽&nbsp;/ шт.
          </div>
        </div>
      </div>

      <div class="order-2 min-w-[21px] lg:order-4 xl:ml-4">
        <NewCheckbox v-model="isSelected" :name="`isProductSelected${cartProductData.id}`" />
      </div>
    </div>

    <div class="flex w-full flex-col">
      <CartProductNotification
        v-if="cartProductData.attributes.warning"
        class="mb-2"
        :notification="cartProductData.attributes.warning"
      />

      <CartProductNotification
        v-if="cartProductData.attributes.isOnlyAvailableInMoscow"
        class="mb-2"
        :notification="'Доставляется только по\x0AМоскве.'"
      />

      <CartProductNotification
        v-if="
          availableProductsCount !== undefined
            && availableProductsCount !== null
            && (availableProductsCount === 0 || availableProductsCount < quantity)
        "
        class="mb-2"
        :notification="
          availableProductsCount === 0
            ? 'Нет в\x0Aналичии.'
            : `${helpers.getDeclension(availableProductsCount, constants.wordForms.left)} ${availableProductsCount}\x0Aшт`
        "
      />
    </div>
  </div>
</template>
